import axios from 'axios';
import { Dispatch } from 'redux';

// Action Types
const GET_CONTINENTS = 'GET_CONTINENTS';
const GET_CONTINENT_DATA = 'GET_CONTINENT_DATA';
const GET_CONTINENT_CMS_DATA = 'GET_CONTINENT_CMS_DATA';


// Action Interfaces
interface GetContinentsAction {
  type: typeof GET_CONTINENTS;
  payload: Continent[];
}

interface GetContinentDataAction { // New interface for the continent data action
  type: typeof GET_CONTINENT_DATA;
  payload: ContinentData;
}

interface GetContinentCmsDataAction {
  type: typeof GET_CONTINENT_CMS_DATA;
  payload: ContinentCmsData[];
}

// Define the Continent type
interface Continent {
  code: string;
  name: string;
  slug: string;
  content_cms_entry__: string;
  order: number;
  activated: boolean;
}

interface ContinentData {
  code: string;
  name: string;
  slug: string;
  content_cms_entry__: string;
  order: number;
  activated?: boolean;
}

interface ContinentCmsData {
  title: string;
  short_content: string;
  content: string;
  mainImage: string;
}

// Async Action Creator
export const getContinents = (activated?: boolean, language?: string) => async (dispatch: Dispatch<GetContinentsAction>) => {
  try {
    // Construct query parameters based on 'activated' argument
    const params: Record<string, any> = activated !== undefined ? { activated } : {};
    if (language) {
      params.language = language;
    }

    const response = await axios.get(`${process.env.NEXT_PUBLIC_SOCKET}/api/continents`, { params });

    if (response.data) {
      dispatch({
        type: GET_CONTINENTS,
        payload: response.data.sort((a: Continent, b: Continent) => a.order - b.order), // Sorting by 'order'
      });
    } else {
      // Handle the case when data is not available
      // You can dispatch another action or handle it as needed
    }
  } catch (error) {
    console.error('Error fetching continents:', error);
    // You can dispatch an error action or handle it as needed
  }
};

export const getContinentData = (slug: string) => async (dispatch: Dispatch<GetContinentDataAction>) => {
  try {
    const response = await axios.get(`${process.env.NEXT_PUBLIC_SOCKET}/api/continent/data`, {
      params: { slug: slug }
    });

    if (response.data) {
      dispatch({
        type: GET_CONTINENT_DATA,
        payload: response.data,
      });
    } else {
      // Handle the case when data is not available
      // You can dispatch another action or handle it as needed
    }
  } catch (error) {
    console.error('Error fetching continent data:', error);
    // You can dispatch an error action or handle it as needed
  }
};

export const getContinentCmsData = (content_cms_entry__: string, language: string) => async (dispatch: Dispatch<GetContinentCmsDataAction>) => {
  try {
    const response = await axios.get(`${process.env.NEXT_PUBLIC_SOCKET}/api/continent/cms/data`, {
      params: { content_cms_entry__, language }
    });

    // If the response is successful, dispatch the data to the store
    dispatch({
      type: GET_CONTINENT_CMS_DATA,
      payload: response.data,
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404 && language !== 'en_US') {
      // If there's a 404 error and the language is not already 'en_US', try again with 'en_US'
      try {
        const fallbackResponse = await axios.get(`${process.env.NEXT_PUBLIC_SOCKET}/api/continent/cms/data`, {
          params: { content_cms_entry__, language: 'en_US' }
        });

        dispatch({
          type: GET_CONTINENT_CMS_DATA,
          payload: fallbackResponse.data,
        });
      } catch (fallbackError) {
        console.error('Error fetching continent CMS data with fallback language:', fallbackError);
        // Handle further error (maybe dispatch an error action)
      }
    } else {
      // Handle other errors or a 404 when the language is already 'en_US'
      console.error('Error fetching continent CMS data:', error);
      // Handle error (maybe dispatch an error action)
    }
  }
};


// State Type
interface ContinentState {
  continents: Continent[];
  currentContinentData: ContinentData | null;
  continentCmsData: ContinentCmsData[] | null;
}

// Initial State
const initialState: ContinentState = {
  continents: [],
  currentContinentData: null,
  continentCmsData: null,
};

// Reducer
const continentReducer = (
    state: ContinentState = initialState, 
    action: GetContinentsAction | GetContinentDataAction | GetContinentCmsDataAction // Include the new action type
  ) => {
  switch (action.type) {
    case GET_CONTINENTS: {
      return {
        ...state,
        continents: action.payload,
      };
    }
    case GET_CONTINENT_DATA: {
      const continentDataAction = action as GetContinentDataAction;
      return {
        ...state,
        currentContinentData: continentDataAction.payload,
      };
    }
    case GET_CONTINENT_CMS_DATA: {
      return {
        ...state,
        continentCmsData: action.payload,
      };
    }
    default: {
      return state;
    }
  }
};

export default continentReducer;
